import { useCallback, useEffect, useMemo, useState } from "react";
import UiIcon from "./UiIcon";
import HistoryTrendChart from "./history/HistoryTrendChart";
import HistoryPeriodDetail, { ScoreTrio } from "./history/HistoryPeriodDetail";
import { historyCopy } from "./history/historyCopy";
import {
    changeVsPrevious, chronological, defaultFocusPeriod, formatScore, formatSigned, periodDisplayLabel,
    quarterLabel, seasonAliases, toNumber,
} from "./history/historyModel";
import "../styles/cvi-history.css";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL ?? "").replace(/\/+$/, "");
const documentLanguage = () => (typeof document !== "undefined" && document.documentElement.lang === "fr" ? "fr" : "en");

// Source-of-truth rules for this page:
// - `allPeriods` (the viewer's own results, or the organization mean for executives) comes from
//   final snapshots for locked periods and the live server value for the active period.
// - Period records come from GET /api/periods/:id/history, which returns the final snapshot for
//   a locked period and the live calculation otherwise, scoped by the server to the people the
//   viewer may see. Each period is requested once and reused.
// - Nothing is recalculated here and a missing value is never shown as zero.

function useHistoryRecords(periodIds) {
    const key = periodIds.join("|");
    const [records, setRecords] = useState({});
    const load = useCallback((ids, cancelledRef = { current: false }) => {
        const token = sessionStorage.getItem("cvi_token");
        setRecords(prev => ({ ...prev, ...Object.fromEntries(ids.map(id => [id, { ...(prev[id] || {}), loading: true, error: null }])) }));
        ids.forEach(id => {
            fetch(`${API_BASE_URL}/api/periods/${encodeURIComponent(id)}/history`, { headers: { Authorization: `Bearer ${token}` } })
                .then(async response => {
                    const json = await response.json().catch(() => ({}));
                    if (!response.ok) throw new Error(json.message || "History could not be loaded");
                    return json;
                })
                .then(data => { if (!cancelledRef.current) setRecords(prev => ({ ...prev, [id]: { data, loading: false, error: null } })); })
                .catch(error => {
                    console.error("History record load failed:", id, error);
                    if (!cancelledRef.current) setRecords(prev => ({ ...prev, [id]: { data: null, loading: false, error } }));
                });
        });
    }, []);
    useEffect(() => {
        const cancelledRef = { current: false };
        if (key) load(key.split("|"), cancelledRef);
        return () => { cancelledRef.current = true; };
    }, [key, load]);
    return { records, reload: id => load([id]) };
}

const positionKey = person => String(person?.positionId || person?.positionName || person?.role || "");

export default function HistoryArchivePage({ allPeriods = [], user, onOpenSummary, language: languageProp, scope = "personal" }) {
    const language = languageProp || documentLanguage();
    const c = historyCopy(language);
    const periodIds = useMemo(() => allPeriods.map(p => String(p.periodId)).filter(Boolean), [allPeriods]);
    const { records, reload } = useHistoryRecords(periodIds);

    // Server metadata (dates, locked/active) is authoritative for each period's status.
    const chron = useMemo(() => chronological(allPeriods.map(p => {
        const meta = records[String(p.periodId)]?.data?.period;
        return meta ? { ...p, startDate: meta.startDate, endDate: meta.endDate, status: meta.status, locked: meta.locked } : p;
    })), [allPeriods, records]);

    const [view, setView] = useState("period");
    const [focusId, setFocusId] = useState(null);
    const [detailId, setDetailId] = useState(null);
    const [yearFilter, setYearFilter] = useState("all");
    const [seasonFilter, setSeasonFilter] = useState("all");
    const [subjectId, setSubjectId] = useState(null);
    const [roleFilter, setRoleFilter] = useState("all");
    const [personFilter, setPersonFilter] = useState("all");
    const [personId, setPersonId] = useState(null);

    const focus = chron.find(p => String(p.periodId) === String(focusId)) || defaultFocusPeriod(chron);
    const focusChanges = focus ? {
        cvi: changeVsPrevious(chron, focus.periodId, "cvi"),
        individual: changeVsPrevious(chron, focus.periodId, "individual"),
        org: changeVsPrevious(chron, focus.periodId, "org"),
    } : {};

    const years = useMemo(() => [...new Set(chron.map(p => p._meta.year).filter(Boolean))].sort((a, b) => Number(b) - Number(a)), [chron]);
    const seasons = useMemo(() => seasonAliases.map(s => s.key).filter(s => chron.some(p => p._meta.season === s)), [chron]);
    const tableRows = useMemo(() => chron
        .filter(p => (yearFilter === "all" || p._meta.year === yearFilter) && (seasonFilter === "all" || p._meta.season === seasonFilter))
        .slice().reverse(), [chron, yearFilter, seasonFilter]);

    // Everyone the viewer may see in any period, built from the same period records.
    const peopleIndex = useMemo(() => {
        const byId = new Map();
        chron.forEach(p => (records[String(p.periodId)]?.data?.people || []).forEach(person => {
            const id = String(person.userId);
            if (!byId.has(id)) byId.set(id, { userId: person.userId, name: person.name, role: person.role, positionName: person.positionName, positionId: person.positionId, periods: [] });
            byId.get(id).periods.push({ period: p, person });
        }));
        return [...byId.values()].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), c.locale));
    }, [chron, records, c.locale]);
    const recordsLoading = periodIds.some(id => records[id]?.loading || !records[id]);
    const canViewPeople = peopleIndex.length > 1;
    const personSeries = person => chronological((person?.periods || []).map(({ period, person: row }) => ({
        periodId: period.periodId, label: period.label, startDate: period.startDate, endDate: period.endDate,
        status: period.status, locked: period.locked,
        cvi: toNumber(row.scores?.cvi), individual: toNumber(row.scores?.individual), org: toNumber(row.scores?.organizational),
    })));

    // ── Detail ─────────────────────────────────────────────────────────────────────────
    const detailPeriod = chron.find(p => String(p.periodId) === String(detailId)) || null;
    const detailRecord = detailId ? records[String(detailId)] : null;
    const detailData = detailRecord?.data || null;
    const detailPeople = useMemo(() => detailData?.people || [], [detailData]);
    const roleOptions = useMemo(() => {
        const map = new Map();
        detailPeople.forEach(person => { const k = positionKey(person); if (k && !map.has(k)) map.set(k, person.positionName || person.role || "—"); });
        return [...map.entries()].map(([value, label]) => ({ value, label }));
    }, [detailPeople]);
    const filteredPeople = roleFilter === "all" ? detailPeople : detailPeople.filter(person => positionKey(person) === roleFilter);
    const effectiveSubject = filteredPeople.some(p => String(p.userId) === String(subjectId)) ? subjectId
        : (filteredPeople.find(p => String(p.userId) === String(detailData?.currentUserId || user?.id))?.userId || filteredPeople[0]?.userId || null);
    const selected = filteredPeople.find(p => String(p.userId) === String(effectiveSubject)) || null;
    const subjectSeries = personSeries(peopleIndex.find(p => String(p.userId) === String(effectiveSubject)));
    const subjectChanges = detailId ? {
        cvi: changeVsPrevious(subjectSeries, detailId, "cvi"),
        individual: changeVsPrevious(subjectSeries, detailId, "individual"),
        org: changeVsPrevious(subjectSeries, detailId, "org"),
    } : {};
    const detailIndex = chron.findIndex(p => String(p.periodId) === String(detailId));

    const openDetail = (periodId, preferredSubject = null) => {
        setDetailId(String(periodId));
        setSubjectId(preferredSubject);
        setRoleFilter("all");
        if (typeof window !== "undefined") document.querySelector(".cvi-role-scroll")?.scrollTo?.({ top: 0 });
    };
    const closeDetail = () => setDetailId(null);
    const original = periodId => allPeriods.find(p => String(p.periodId) === String(periodId));

    if (detailId && detailPeriod) {
        return <div className="adm-page cvi-history" data-cvi-i18n="managed" lang={language}>
            <HistoryPeriodDetail c={c} language={language} period={detailPeriod} state={detailPeriod._state}
                data={detailData} loading={!detailRecord || (detailRecord.loading && !detailData)} error={detailRecord?.error && !detailData}
                onRetry={() => reload(String(detailId))} onBack={closeDetail}
                prevPeriod={detailIndex > 0 ? chron[detailIndex - 1] : null} nextPeriod={detailIndex >= 0 && detailIndex < chron.length - 1 ? chron[detailIndex + 1] : null}
                onPrev={() => openDetail(chron[detailIndex - 1].periodId, effectiveSubject)} onNext={() => openDetail(chron[detailIndex + 1].periodId, effectiveSubject)}
                people={filteredPeople} roleOptions={roleOptions} roleFilter={roleFilter} onRoleFilter={value => { setRoleFilter(value); setSubjectId(null); }}
                subjectId={effectiveSubject} onSubject={setSubjectId} selected={selected} changes={subjectChanges} />
        </div>;
    }

    // ── Person view ────────────────────────────────────────────────────────────────────
    const personRoleOptions = (() => {
        const map = new Map();
        peopleIndex.forEach(p => { const k = positionKey(p); if (k && !map.has(k)) map.set(k, p.positionName || p.role || "—"); });
        return [...map.entries()].map(([value, label]) => ({ value, label }));
    })();
    const visiblePeople = personFilter === "all" ? peopleIndex : peopleIndex.filter(p => positionKey(p) === personFilter);
    const person = visiblePeople.find(p => String(p.userId) === String(personId)) || visiblePeople[0] || null;
    const personRows = personSeries(person);

    const statusBadge = state => <span className={`cvi-history-state is-${state}`}><i aria-hidden="true" />{c.status[state]}</span>;
    const changeCell = row => {
        const ch = changeVsPrevious(row.series || chron, row.periodId, "cvi");
        if (ch.withheld) return <span className="cvi-history-muted">{c.table.withheld}</span>;
        if (ch.value == null) return <span className="cvi-history-muted">—</span>;
        const tone = Math.abs(ch.value) < 0.05 ? "flat" : ch.value > 0 ? "up" : "down";
        return <span className={`cvi-history-delta is-${tone}`}>{formatSigned(ch.value, language)}</span>;
    };

    return <div className="adm-page cvi-history" data-cvi-i18n="managed" lang={language}>
        <header className="adm-page-header">
            <div className="adm-page-header-copy">
                <h1 className="adm-title">{c.title}</h1>
                <p className="adm-desc">{c.subtitle[scope] || c.subtitle.personal}</p>
            </div>
            <div className="adm-header-actions">
                {view === "period" && chron.length > 0 && <label className="cvi-history-focus">
                    <span>{c.focusLabel}</span>
                    <select value={focus?.periodId || ""} onChange={event => setFocusId(event.target.value)}>
                        {chron.slice().reverse().map(p => <option key={p.periodId} value={p.periodId}>{periodDisplayLabel(p, language)} · {c.status[p._state]}</option>)}
                    </select>
                </label>}
                {canViewPeople && <div className="adm-segmented" role="radiogroup" aria-label={c.views.label}>
                    <button type="button" role="radio" aria-checked={view === "period"} onClick={() => setView("period")}>{c.views.period}</button>
                    <button type="button" role="radio" aria-checked={view === "person"} onClick={() => setView("person")}>{c.views.person}</button>
                </div>}
            </div>
        </header>

        {view === "person" ? <>
            <section className="cvi-history-card">
                <div className="cvi-history-card-head"><div><h2>{c.person.title}</h2><p>{c.person.subtitle}</p></div></div>
                {recordsLoading && !peopleIndex.length ? <div className="cvi-history-skeleton"><span /><span /></div> : <div className="cvi-history-filters">
                    {personRoleOptions.length > 1 && <label className="adm-field"><span>{c.person.position}</span>
                        <select value={personFilter} onChange={event => { setPersonFilter(event.target.value); setPersonId(null); }}>
                            <option value="all">{c.person.allRoles}</option>
                            {personRoleOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select></label>}
                    <label className="adm-field"><span>{c.person.employee}</span>
                        <select value={person?.userId || ""} onChange={event => setPersonId(event.target.value)}>
                            {visiblePeople.map(p => <option key={p.userId} value={p.userId}>{p.name}{p.positionName ? ` — ${p.positionName}` : ""}</option>)}
                        </select></label>
                </div>}
            </section>
            {!person ? <section className="cvi-history-card"><div className="cvi-history-empty">{recordsLoading ? c.person.loading : c.person.empty}</div></section> : <>
                <section className="cvi-history-card">
                    <div className="cvi-history-card-head"><div><h2>{person.name}</h2><p>{[person.positionName, c.person.periods(personRows.length)].filter(Boolean).join(" · ")}</p></div></div>
                    <HistoryTrendChart periods={personRows} c={c} language={language} selectedId={null} onSelect={periodId => openDetail(periodId, person.userId)} />
                </section>
                <PeriodTable c={c} language={language} rows={personRows.slice().reverse().map(row => ({ ...row, series: personRows }))}
                    statusBadge={statusBadge} changeCell={changeCell} onDetails={row => openDetail(row.periodId, person.userId)} />
            </>}
        </> : chron.length === 0 ? <section className="cvi-history-card"><div className="cvi-history-empty">{c.table.empty}</div></section> : <>
            <section className="cvi-history-card cvi-history-overview" aria-labelledby="cvi-history-overview-title">
                <div className="cvi-history-card-head">
                    <div><h2 id="cvi-history-overview-title">{c.overview.title[scope] || c.overview.title.personal}</h2>
                        <p>{periodDisplayLabel(focus, language)} · {c.statusHelp[focus._state]}{scope === "organization" ? ` · ${c.overview.mean}` : ""}</p></div>
                    <div className="cvi-history-card-actions">
                        {statusBadge(focus._state)}
                        <button type="button" className="adm-btn adm-btn--sm" onClick={() => openDetail(focus.periodId)} data-history-action="details">
                            {c.table.details}<UiIcon name="chevronRight" size={14} /></button>
                    </div>
                </div>
                <ScoreTrio c={c} language={language} scores={focus} changes={focusChanges} state={focus._state} />
            </section>

            <section className="cvi-history-card" aria-labelledby="cvi-history-trend-title">
                <div className="cvi-history-card-head"><div><h2 id="cvi-history-trend-title">{c.trend.title}</h2><p>{c.trend.subtitle}</p></div></div>
                <HistoryTrendChart periods={chron} c={c} language={language} selectedId={focus?.periodId} onSelect={setFocusId} />
            </section>

            <PeriodTable c={c} language={language} rows={tableRows} focusId={focus?.periodId} onFocus={setFocusId}
                statusBadge={statusBadge} changeCell={changeCell} onDetails={row => openDetail(row.periodId)}
                onSummary={onOpenSummary ? row => onOpenSummary(original(row.periodId)) : null}
                filters={(years.length > 1 || seasons.length > 1) && <div className="cvi-history-filters is-inline">
                    <label className="adm-field"><span>{c.table.year}</span>
                        <select value={yearFilter} onChange={event => setYearFilter(event.target.value)}>
                            <option value="all">{c.table.allYears}</option>{years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select></label>
                    <label className="adm-field"><span>{c.table.quarter}</span>
                        <select value={seasonFilter} onChange={event => setSeasonFilter(event.target.value)}>
                            <option value="all">{c.table.allQuarters}</option>{seasons.map(s => <option key={s} value={s}>{quarterLabel(s, language)}</option>)}
                        </select></label>
                    {(yearFilter !== "all" || seasonFilter !== "all") && <button type="button" className="adm-btn adm-btn--sm" onClick={() => { setYearFilter("all"); setSeasonFilter("all"); }}>{c.table.clear}</button>}
                </div>} />
        </>}
    </div>;
}

function PeriodTable({ c, language, rows, focusId = null, onFocus, statusBadge, changeCell, onDetails, onSummary, filters }) {
    const t = c.table;
    return <section className="cvi-history-card" aria-labelledby="cvi-history-table-title">
        <div className="cvi-history-card-head">
            <div><h2 id="cvi-history-table-title">{t.title}</h2><p>{t.subtitle}</p></div>
            <span className="cvi-history-muted">{t.count(rows.length)}</span>
        </div>
        {filters}
        {rows.length === 0 ? <div className="cvi-history-empty">{t.noMatch}</div> : <div className="adm-table-wrap">
            <table className="cvi-history-periods">
                <thead><tr>
                    <th scope="col">{t.period}</th><th scope="col">{t.status}</th>
                    <th scope="col" className="is-num">{t.cvi}</th><th scope="col" className="is-num">{t.individual}</th>
                    <th scope="col" className="is-num">{t.collective}</th><th scope="col" className="is-num">{t.change}</th>
                    <th scope="col" className="is-actions"><span className="adm-sr">{t.actions}</span></th>
                </tr></thead>
                <tbody>{rows.map(row => {
                    const isFocus = String(row.periodId) === String(focusId);
                    return <tr key={row.periodId} className={isFocus ? "is-focus" : ""} aria-selected={onFocus ? isFocus : undefined}>
                        <th scope="row">{onFocus
                            ? <button type="button" className="cvi-history-period-link" onClick={() => onFocus(row.periodId)} aria-pressed={isFocus}>{periodDisplayLabel(row, language)}</button>
                            : periodDisplayLabel(row, language)}</th>
                        <td>{statusBadge(row._state)}</td>
                        <td className="is-num is-strong">{formatScore(row.cvi, language)}</td>
                        <td className="is-num">{formatScore(row.individual, language)}</td>
                        <td className="is-num">{formatScore(row.org, language)}</td>
                        <td className="is-num">{changeCell(row)}</td>
                        <td className="is-actions">
                            {onSummary && <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" onClick={() => onSummary(row)}>{t.summary}</button>}
                            <button type="button" className="adm-btn adm-btn--sm" onClick={() => onDetails(row)}>{t.details}</button>
                        </td>
                    </tr>;
                })}</tbody>
            </table>
        </div>}
    </section>;
}
